import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  FullTimelineRange,
  FullTimeRange,
  InitialTimelineRange,
} from "../../types/timeline";
import timelineService from "../../service/timelineService";
import { TimelineData } from "../../utils/dateUtil";

type ZoomAction = {
  payload: number;
};

export type TimelineRangeAction = {
  payload: InitialTimelineRange;
};

export interface TimelineSlice {
  defaultPixelPerMinute: number; //only mutated on first render by setDefaultPixelsPerMinute
  zoomLevel: number;
  distanceFromTop?: number;
  pixelPerMinute: number;
  pixelsPerHour?: number;
  timelineArrayOfTimeString?: TimelineData[];
  timelineRange?: FullTimelineRange;
  fullWorkDayTimeRange?: FullTimeRange; //Used to come back to initial x´zoom, if needed
}

function applyZoomLevel(state: TimelineSlice) {
  const newPixelsPerMinute = Math.max(
    state.defaultPixelPerMinute * state.zoomLevel,
    0.66,
  );

  state.pixelPerMinute = newPixelsPerMinute;
  state.pixelsPerHour = newPixelsPerMinute * 60;
}

const initialState: TimelineSlice = {
  defaultPixelPerMinute: 1,
  pixelPerMinute: 1,
  zoomLevel: 1,
};

const timelineSlice = createSlice({
  name: "timeline",
  initialState,
  reducers: {
    setDefaultPixelsPerMinute(state, action) {
      state.defaultPixelPerMinute = action.payload;
    },
    setTimelineArrayOfTimeStrings(state, action) {
      state.timelineArrayOfTimeString = action.payload;
    },
    setTimelineRange(state, action: TimelineRangeAction) {
      const hoursInView = timelineService.getHoursInView(
        action.payload.firstHourInView,
        action.payload.lastVisibleHour,
      );

      if (
        state.timelineRange &&
        state.timelineRange.chosenDate === action.payload.chosenDate &&
        state.timelineRange.firstHourInView ===
          action.payload.firstHourInView &&
        state.timelineRange.lastVisibleHour ===
          action.payload.lastVisibleHour &&
        state.timelineRange.fullDayHours === action.payload.fullDayHours
      )
        return;

      state.timelineRange = { ...action.payload, hoursInView: hoursInView };
    },

    zoomInOnVerticalView(state, _action: ZoomAction) {
      state.zoomLevel = state.zoomLevel * 1.5;
      applyZoomLevel(state);
    },

    zoomOutOnVerticalView(state, _action: ZoomAction) {
      // set limit on zoomOut to ensure that first tasks are not hidden behind collapsed KPI bar.
      state.zoomLevel = state.zoomLevel / 1.5 >= 1 ? state.zoomLevel / 1.5 : 1;
      //Zoom out by a factor of 1.5, but don't let the scale drop below 0.66 pixels per minute.
      applyZoomLevel(state);
    },
    zoomToLevel(state, action: PayloadAction<boolean>) {
      if (action.payload) {
        state.zoomLevel = 1;
        state.pixelPerMinute = state.defaultPixelPerMinute;
        state.pixelsPerHour = state.defaultPixelPerMinute * 60;
      } else applyZoomLevel(state);
    },

    setZoomLevel(state, action: PayloadAction<number>) {
      state.zoomLevel = action.payload;
      applyZoomLevel(state);
    },

    jump(state, action) {
      state.timelineRange = {
        ...state.timelineRange!,
        firstHourInView: state.timelineRange!.firstHourInView + action.payload,
        hoursInView: state.timelineRange!.hoursInView,
      };
    },
  },
});

export const {
  setTimelineArrayOfTimeStrings,
  setTimelineRange,
  zoomToLevel,
  zoomInOnVerticalView,
  zoomOutOnVerticalView,
  setZoomLevel,
  jump,
  setDefaultPixelsPerMinute,
} = timelineSlice.actions;

export default timelineSlice.reducer;
